import { useCallback, useEffect, useState } from 'react';

import { MAX_KPI_SELECTION } from 'components/molecules/PackArchitecture/packArchitectureTable.helpers';
import type { PpaMetaControlNode } from 'components/pages/PackArchitecture/packArchitectureMeta.types';

const areStringArraysEqual = (left: string[], right: string[]) =>
    left.length === right.length && left.every((value, index) => value === right[index]);

const toValidSelectedKpis = (
    yAxis: string,
    previousSelection: string[],
    tableKpiOptionItems: PpaMetaControlNode[],
): string[] => {
    const validTableKpiIds = new Set(
        tableKpiOptionItems
            .map((item) => item.id)
            .filter((id): id is string => Boolean(id)),
    );

    const validExtras = previousSelection.filter((id) => id !== yAxis && validTableKpiIds.has(id));

    return [yAxis, ...validExtras].slice(0, MAX_KPI_SELECTION);
};

export function usePackArchitectureTableControls(yAxis: string, tableKpiOptionItems: PpaMetaControlNode[]) {
    const [selectedKpis, setSelectedKpis] = useState<string[]>([]);
    const [tableSearchInput, setTableSearchInput] = useState('');
    const [tableSearchKeyword, setTableSearchKeyword] = useState('');

    const toggleTableKpi = useCallback((kpiId: string) => {
        setSelectedKpis((prev) => {
            if (prev.includes(kpiId)) {
                return prev.length === 1 ? prev : prev.filter((id) => id !== kpiId);
            }
            return prev.length < MAX_KPI_SELECTION ? [...prev, kpiId] : prev;
        });
    }, []);

    const submitTableSearch = useCallback((override?: string) => {
        const nextKeyword = (override !== undefined ? override : tableSearchInput).trim();
        if (override !== undefined) {
            setTableSearchInput(override);
        }
        setTableSearchKeyword(nextKeyword);
    }, [tableSearchInput]);

    useEffect(() => {
        if (!yAxis) return;

        setSelectedKpis((previous) => {
            const next =
                previous.length === 0
                    ? [yAxis]
                    : toValidSelectedKpis(yAxis, previous, tableKpiOptionItems);

            return areStringArraysEqual(previous, next) ? previous : next;
        });
    }, [yAxis, tableKpiOptionItems]);

    return {
        selectedKpis,
        toggleTableKpi,
        tableSearchInput,
        setTableSearchInput,
        submitTableSearch,
        tableSearchKeyword,
    };
}
